<template>
	<div class="live-page">
		<div class="live-header">
			<div class="live-header__title">
				<span class="live-header__icon">
					<i class="fas fa-tv"></i>
				</span>
				<div>
					<h1>
						{{
							$store.state.language === "en"
								? "Live Matches"
								: "တိုက်ရိုက်ပွဲများ"
						}}
					</h1>
					<p>
						{{
							$store.state.language === "en"
								? "Watch free football streams"
								: "အခမဲ့ ဘောလုံးတိုက်ရိုက်ကြည့်ရှုရန်"
						}}
					</p>
				</div>
			</div>
			<button class="refresh-btn" type="button" @click="fetchMatches" :disabled="loading">
				<i class="fas fa-sync-alt" :class="{ spinning: loading }"></i>
			</button>
		</div>

		<div class="tabs-wrap" v-if="!loading || hasMatches">
			<div class="tabs">
				<button
					class="tab-button"
					:class="{ active: tab === 'Live' }"
					type="button"
					@click="openTab('Live')"
				>
					<span class="tab-dot" v-if="liveMatches.length"></span>
					{{ $store.state.language === "en" ? "Live" : "တိုက်ရိုက်" }}
					<span class="tab-count">{{ liveMatches.length }}</span>
				</button>
				<button
					class="tab-button"
					:class="{ active: tab === 'Pending' }"
					type="button"
					@click="openTab('Pending')"
				>
					{{
						$store.state.language === "en" ? "Coming soon" : "မကြာမီ"
					}}
					<span class="tab-count">{{ pendingMatches.length }}</span>
				</button>
			</div>
		</div>

		<div class="state-box" v-if="loading && !hasMatches">
			<Loading color="#ffffff" size="28" class="mb-3" />
			<p class="state-title">
				{{
					$store.state.language === "en"
						? "Fetching matches..."
						: "ပွဲစဉ်များ ရယူနေပါသည်..."
				}}
			</p>
			<p class="state-sub">
				{{
					$store.state.language === "en"
						? "Please wait a moment"
						: "ခေတ္တစောင့်ဆိုင်းပေးပါ"
				}}
			</p>
		</div>

		<div class="state-box" v-else-if="!loading && !currentMatches.length">
			<div class="empty-icon">
				<i class="far fa-futbol"></i>
			</div>
			<p class="state-title">
				{{
					tab === "Live"
						? $store.state.language === "en"
							? "No live matches now"
							: "ယခု တိုက်ရိုက်ပွဲ မရှိသေးပါ"
						: $store.state.language === "en"
						? "No upcoming matches"
						: "လာမည့်ပွဲ မရှိသေးပါ"
				}}
			</p>
			<button class="retry-btn" type="button" @click="fetchMatches">
				{{ $store.state.language === "en" ? "Refresh" : "ပြန်စစ်မည်" }}
			</button>
		</div>

		<div class="match-list" v-else>
			<article
				class="match-card"
				v-for="(item, itemKey) in currentMatches"
				:key="`${item.match_time}-${item.home_team_name}-${itemKey}`"
				@click="openMatch(item)"
			>
				<div class="match-card__top">
					<div class="league">
						<img
							v-if="item.league_logo"
							:src="item.league_logo"
							:alt="item.league_name"
							class="league-logo"
						/>
						<span>{{ item.league_name }}</span>
					</div>
					<span class="status-pill live" v-if="isLive(item)">
						<span class="pulse"></span>
						LIVE
					</span>
					<span class="status-pill soon" v-else>
						{{ formatMatchTime(item.match_time) }}
					</span>
				</div>

				<div class="match-card__body">
					<div class="team">
						<img :src="item.home_team_logo" :alt="item.home_team_name" />
						<strong>{{ item.home_team_name }}</strong>
					</div>

					<div class="vs-block">
						<span class="vs">VS</span>
						<small v-if="!isLive(item)">{{ formatMatchDay(item.match_time) }}</small>
					</div>

					<div class="team">
						<img :src="item.away_team_logo" :alt="item.away_team_name" />
						<strong>{{ item.away_team_name }}</strong>
					</div>
				</div>

				<div class="match-card__footer">
					<span class="servers-hint" v-if="hasServers(item)">
						<i class="fas fa-signal mr-1"></i>
						{{ item.servers.length }}
						{{
							$store.state.language === "en"
								? "servers"
								: "ချန်နယ်"
						}}
					</span>
					<span class="servers-hint muted" v-else>
						{{
							$store.state.language === "en"
								? "Stream soon"
								: "ကြည့်ရှုရန် စောင့်ဆိုင်း"
						}}
					</span>
					<span class="watch-cta">
						{{
							isLive(item)
								? $store.state.language === "en"
									? "Watch"
									: "ကြည့်မည်"
								: $store.state.language === "en"
								? "Details"
								: "အသေးစိတ်"
						}}
						<i class="fas fa-chevron-right ml-1"></i>
					</span>
				</div>
			</article>
		</div>
	</div>
</template>

<script>
import { Loading } from "vant";
import { mapActions } from "vuex";

const LIVE_MATCHES_API = "https://footballapi.spacetechmm.com/free-live-matches";

export default {
	components: { Loading },
	name: "Live",
	data: () => ({
		tab: "Live",
		liveMatches: [],
		pendingMatches: [],
		loading: false,
	}),
	computed: {
		currentMatches() {
			return this.tab === "Live" ? this.liveMatches : this.pendingMatches;
		},
		hasMatches() {
			return this.liveMatches.length > 0 || this.pendingMatches.length > 0;
		},
	},
	methods: {
		...mapActions(["fetchUser"]),
		openTab(tabName) {
			this.tab = tabName;
		},
		isLive(match) {
			return match?.match_status?.toLowerCase() === "live";
		},
		hasServers(match) {
			return Array.isArray(match?.servers) && match.servers.length > 0;
		},
		formatMatchTime(matchTime) {
			if (matchTime == null || matchTime === "") return "";
			const timestamp = Number(matchTime);
			if (!Number.isNaN(timestamp) && timestamp > 1000000000) {
				return moment.unix(timestamp).format("HH:mm");
			}
			return matchTime;
		},
		formatMatchDay(matchTime) {
			const timestamp = Number(matchTime);
			if (!Number.isNaN(timestamp) && timestamp > 1000000000) {
				return moment.unix(timestamp).format("D MMM");
			}
			return "";
		},
		openMatch(item) {
			if (!this.isLive(item) || !this.hasServers(item)) return;
			sessionStorage.setItem(
				"score96_selected_live_match",
				JSON.stringify(item)
			);
			this.$router.push({
				path: "/video-links",
				query: {
					home: item.home_team_name,
					away: item.away_team_name,
					match_time: String(item.match_time),
					tab: this.tab,
				},
			});
		},
		async fetchMatches() {
			this.loading = true;
			try {
				const res = await axios.get(LIVE_MATCHES_API, {
					transformRequest: [
						(data, headers) => {
							delete headers.Authorization;
							return data;
						},
					],
				});
				const matches = Array.isArray(res?.data)
					? res.data
					: res?.data?.data || [];
				this.liveMatches = matches.filter(
					(match) => this.isLive(match) && this.hasServers(match)
				);
				this.pendingMatches = matches.filter((match) => !this.isLive(match));
			} catch (error) {
				console.log(error);
			} finally {
				this.loading = false;
			}
		},
	},
	async mounted() {
		const authUser = await this.fetchUser();
		if (!authUser) {
			this.$router.push("/auth/login");
			return;
		}
		this.fetchMatches();
	},
};
</script>

<style scoped>
.live-page {
	min-height: calc(100vh - 120px);
	padding: 1rem 0.9rem 5.5rem;
	background: #13131d;
}

.live-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	margin-bottom: 1rem;
}

.live-header__title {
	display: flex;
	align-items: center;
	gap: 12px;
}

.live-header__icon {
	width: 44px;
	height: 44px;
	border-radius: 14px;
	display: grid;
	place-items: center;
	background: linear-gradient(145deg, #2a2d3e, #1f212e);
	color: #fff;
	box-shadow: 0 8px 18px rgba(0, 0, 0, 0.35);
}

.live-header h1 {
	margin: 0;
	font-size: 1.25rem;
	font-weight: 800;
	color: #ffffff;
	line-height: 1.2;
}

.live-header p {
	margin: 2px 0 0;
	font-size: 0.8rem;
	color: #bbbdc3;
}

.refresh-btn {
	width: 40px;
	height: 40px;
	border: none;
	border-radius: 12px;
	background: #1f212e;
	color: #ffffff;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}

.refresh-btn:disabled {
	opacity: 0.7;
}

.spinning {
	animation: spin 0.8s linear infinite;
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}

.tabs-wrap {
	display: flex;
	justify-content: center;
	margin-bottom: 1rem;
}

.tabs {
	display: flex;
	width: 100%;
	max-width: 360px;
	padding: 4px;
	gap: 4px;
	background: #1f212e;
	border-radius: 14px;
	box-shadow: 0 4px 14px rgba(0, 0, 0, 0.2);
}

.tab-button {
	flex: 1;
	border: none;
	border-radius: 11px;
	background: transparent;
	padding: 10px 8px;
	font-size: 0.95rem;
	font-weight: 700;
	color: #bbbdc3;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 6px;
}

.tab-button.active {
	background: #ffffff;
	color: #13131d;
}

.tab-dot {
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background: #ff3b3b;
	box-shadow: 0 0 0 0 rgba(255, 59, 59, 0.6);
	animation: pulse-dot 1.6s infinite;
}

.tab-button.active .tab-dot {
	background: #ff3b3b;
}

.tab-count {
	min-width: 22px;
	height: 22px;
	padding: 0 6px;
	border-radius: 999px;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	font-size: 0.75rem;
	background: rgba(255, 255, 255, 0.08);
}

.tab-button.active .tab-count {
	background: rgba(19, 19, 29, 0.08);
}

.state-box {
	margin-top: 3rem;
	text-align: center;
	padding: 1.5rem 1rem;
}

.state-title {
	margin: 0;
	font-weight: 700;
	color: #ffffff;
}

.state-sub {
	margin: 0.35rem 0 0;
	color: #bbbdc3;
	font-size: 0.9rem;
}

.empty-icon {
	width: 64px;
	height: 64px;
	margin: 0 auto 0.85rem;
	border-radius: 50%;
	display: grid;
	place-items: center;
	background: #1f212e;
	color: #ffffff;
	font-size: 1.5rem;
	box-shadow: 0 6px 16px rgba(0, 0, 0, 0.25);
}

.retry-btn {
	margin-top: 1rem;
	border: none;
	border-radius: 999px;
	padding: 0.55rem 1.2rem;
	background: #ffffff;
	color: #13131d;
	font-weight: 700;
}

.match-list {
	display: grid;
	gap: 0.85rem;
}

.match-card {
	background: #1f212e;
	border-radius: 16px;
	padding: 0.9rem 0.95rem 0.75rem;
	box-shadow: 0 8px 22px rgba(0, 0, 0, 0.25);
	border: 1px solid rgba(255, 255, 255, 0.06);
	cursor: pointer;
	transition: transform 0.18s ease, box-shadow 0.18s ease;
}

.match-card:active {
	transform: scale(0.985);
}

.match-card__top {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 10px;
	margin-bottom: 0.85rem;
}

.league {
	display: flex;
	align-items: center;
	gap: 8px;
	min-width: 0;
	font-size: 0.82rem;
	font-weight: 700;
	color: #bbbdc3;
}

.league span {
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.league-logo {
	width: 22px;
	height: 22px;
	object-fit: contain;
	flex-shrink: 0;
}

.status-pill {
	flex-shrink: 0;
	display: inline-flex;
	align-items: center;
	gap: 5px;
	border-radius: 999px;
	padding: 0.28rem 0.65rem;
	font-size: 0.72rem;
	font-weight: 800;
	letter-spacing: 0.03em;
}

.status-pill.live {
	background: rgba(220, 38, 38, 0.18);
	color: #ff6b6b;
}

.status-pill.soon {
	background: rgba(255, 255, 255, 0.08);
	color: #ffffff;
}

.pulse {
	width: 7px;
	height: 7px;
	border-radius: 50%;
	background: #ff3b3b;
	animation: pulse-dot 1.6s infinite;
}

@keyframes pulse-dot {
	0% {
		box-shadow: 0 0 0 0 rgba(255, 59, 59, 0.55);
	}
	70% {
		box-shadow: 0 0 0 8px rgba(255, 59, 59, 0);
	}
	100% {
		box-shadow: 0 0 0 0 rgba(255, 59, 59, 0);
	}
}

.match-card__body {
	display: grid;
	grid-template-columns: 1fr auto 1fr;
	align-items: center;
	gap: 8px;
}

.team {
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	gap: 8px;
	min-width: 0;
}

.team img {
	width: 56px;
	height: 56px;
	object-fit: contain;
}

.team strong {
	font-size: 0.82rem;
	line-height: 1.25;
	color: #ffffff;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;
	overflow: hidden;
	min-height: 2.1em;
}

.vs-block {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 4px;
}

.vs {
	width: 42px;
	height: 42px;
	border-radius: 50%;
	display: grid;
	place-items: center;
	background: #13131d;
	color: #fff;
	font-size: 0.72rem;
	font-weight: 800;
	box-shadow: 0 6px 14px rgba(0, 0, 0, 0.3);
}

.vs-block small {
	color: #bbbdc3;
	font-size: 0.7rem;
	font-weight: 600;
}

.match-card__footer {
	margin-top: 0.85rem;
	padding-top: 0.7rem;
	border-top: 1px solid rgba(255, 255, 255, 0.08);
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
}

.servers-hint {
	font-size: 0.78rem;
	font-weight: 600;
	color: #ffffff;
}

.servers-hint.muted {
	color: #bbbdc3;
}

.watch-cta {
	font-size: 0.82rem;
	font-weight: 800;
	color: #ffffff;
	display: inline-flex;
	align-items: center;
}
</style>
